/**
 * React hooks for working with feature flags
 * Provides reactive hooks that automatically update components when feature flags change.
 */
import { useState, useEffect } from "react";
import {
  CASINO_PROVIDERS_INNER_FLAGS,
  SPORTSBOOK_PROVIDERS_INNER_FLAGS,
} from "../utils/Constants";
import { TRIVELTA_PRODUCTS } from "../utils/products";
import featureFlagService from "../services/FeatureFlagService";

/**
 * Hook to check if a PAM feature is enabled
 *
 * Reactive hook that automatically updates when feature flags change.
 * Returns loading state during initial fetch and error state on failures.
 *
 * @param {string} featureName - Name of the feature to check (e.g., 'reports', 'promotions')
 * @returns {{ isEnabled: boolean | null, loading: boolean, error: Error | null }} Hook result:
 *   `isEnabled` is true/false once resolved, null while loading; `loading` is
 *   true during the initial fetch; `error` is set on failure, null otherwise.
 *
 * @example
 * function ReportsButton() {
 *   const { isEnabled, loading, error } = usePamFeature('reports');
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <ErrorMessage />;
 *   if (!isEnabled) return null;
 *
 *   return <button>View Reports</button>;
 * }
 */
export function usePamFeature(featureName) {
  const [isEnabled, setIsEnabled] = useState(null); // null = loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkFeature = async () => {
      try {
        setLoading(true);
        setError(null);
        await featureFlagService.initialize();
        const enabled = featureFlagService.isEnabled(featureName);
        setIsEnabled(enabled);
      } catch (err) {
        setError(err);
        setIsEnabled(false); // Default to disabled on error
      } finally {
        setLoading(false);
      }
    };

    checkFeature();
  }, [featureName]);

  return { isEnabled, loading, error };
}

/**
 * Hook that resolves which Manual Actions / Agent Audit Log KPI sections
 * should be visible based on Trivelta Product activation + provider flags.
 *
 * - Casino: requires the Casino product (casino) + at least one casino provider flag
 * - Pikkem: requires the Pikkem product
 * - Sportsbook: requires the Sportsbook product + at least one sportsbook provider flag
 *
 * @returns {{ showCasinoKpi: boolean, showPikkemKpi: boolean, showSportsbookKpi: boolean }}
 */
export function useManualActionsFlags() {
  const [showCasinoKpi, setShowCasinoKpi] = useState(false);
  const [showPikkemKpi, setShowPikkemKpi] = useState(false);
  const [showSportsbookKpi, setShowSportsbookKpi] = useState(false);

  useEffect(() => {
    const checkFlags = async () => {
      await featureFlagService.initialize();
      // TODO(PAMENG-1820): drop the fallback second arg after rollout.
      const casinoActive = featureFlagService.isProductActive(
        TRIVELTA_PRODUCTS.CASINO,
        "casino",
      );
      const hasCasinoProvider = CASINO_PROVIDERS_INNER_FLAGS.some((flag) =>
        featureFlagService.isEnabled(flag),
      );
      setShowCasinoKpi(casinoActive && hasCasinoProvider);
      setShowPikkemKpi(
        featureFlagService.isProductActive(
          TRIVELTA_PRODUCTS.PIKKEM,
          "pikkem",
        ),
      );
      const hasSportsbookProvider = SPORTSBOOK_PROVIDERS_INNER_FLAGS.some(
        (flag) => featureFlagService.isEnabled(flag),
      );
      const sportsbookActive = featureFlagService.isProductActive(
        TRIVELTA_PRODUCTS.SPORTSBOOK,
        "sportsbook_v2",
      );
      setShowSportsbookKpi(hasSportsbookProvider && sportsbookActive);
    };
    checkFlags();
  }, []);

  return { showCasinoKpi, showPikkemKpi, showSportsbookKpi };
}

/**
 * Utility function for synchronous feature checking
 *
 * Direct access to feature flag status without React hooks.
 * Use when you need to check features outside of React components.
 *
 * @param {string} featureName - Name of the feature to check
 * @returns {boolean} Feature status (true if enabled, false if disabled)
 *
 * @example
 * // In a utility function or service
 * function generateReport() {
 *   const canGenerateReports = checkPamFeature('reports');
 *   if (!canGenerateReports) {
 *     throw new Error('Reports feature is disabled');
 *   }
 *   // Generate report...
 * }
 */
export function checkPamFeature(featureName) {
  return featureFlagService.isEnabled(featureName);
}

/**
 * Hook to check a product-scoped feature flag from the TCM V2 platform config
 * (`GET /admin-panel/platform/v2/config` → `products.<scope_key>.feature_flags`).
 *
 * These flags are distinct from the v1 `features_status_config` paths that
 * `usePamFeature` reads — a flag like `apple_pay_routing` only ships inside a
 * product's `feature_flags` array. Product-agnostic on purpose: the backend
 * may nest a flag under `common` today and a different product
 * tomorrow, and consumers should not have to care which node carries it.
 *
 * `isEnabled` stays `false` while loading and on error, so an additive UI
 * degrades to the existing behaviour instead of flashing the flagged one.
 *
 * @param {string} flag - Flag name to look up (e.g. 'apple_pay_routing')
 * @param {string} [productKey] - Optional product scope key
 * @returns {{ isEnabled: boolean, loading: boolean, error: Error | null }}
 *
 * @example
 * const { isEnabled, loading } = useProductFeatureFlag('apple_pay_routing');
 */
export function useProductFeatureFlag(flag, productKey) {
  const [isEnabled, setIsEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const checkFlag = async () => {
      try {
        setLoading(true);
        setError(null);
        await featureFlagService.initialize();
        if (cancelled) return;
        setIsEnabled(checkProductFeatureFlag(flag, productKey));
      } catch (err) {
        if (cancelled) return;
        setError(err);
        setIsEnabled(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    checkFlag();
    return () => {
      cancelled = true;
    };
  }, [flag, productKey]);

  return { isEnabled, loading, error };
}

/**
 * Synchronous variant of `useProductFeatureFlag` for use outside React.
 * Returns `false` before `FeatureFlagService.initialize()` has resolved
 * (no products cached yet).
 *
 * @param {string} flag - Flag name to look up
 * @param {string} [productKey] - Optional product scope key
 * @returns {boolean} True when the flag appears in any product's feature_flags
 */
export function checkProductFeatureFlag(flag, productKey) {
  if (!flag) {
    return false;
  }
  const products = featureFlagService.getProducts() ?? {};
  if (productKey) {
    const featureFlags = products[productKey]?.feature_flags;
    return Array.isArray(featureFlags) && featureFlags.includes(flag);
  }
  return Object.values(products).some(
    (product) =>
      Array.isArray(product?.feature_flags) &&
      product.feature_flags.includes(flag),
  );
}
